import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Section, Spinner } from "@/components/common";

const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function TrackingReset({ trackingStart }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(() => toLocalInput());
  const [confirm, setConfirm] = useState("");
  const m = useMutation({
    mutationFn: () => api.post("/admin/reset-tracking", { tracking_start: new Date(when).toISOString(), confirm }).then((r) => r.data),
    onSuccess: (d) => { toast.success(`Tracked data cleared · now tracking from ${fmtDateTime(d.tracking_start)}`); setOpen(false); setConfirm(""); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Section title="Tracking start & data reset" eyebrow="Go-live control" testId="tracking-section" className="border-red-500/30">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 panel p-3 bg-[#0f172a] min-w-[260px]">
          <CalendarClock className="h-5 w-5 text-blue-300 shrink-0" />
          <div>
            <div className="eyebrow">Tracking since</div>
            <div className="text-sm font-semibold text-slate-100 font-mono" data-testid="tracking-start-value">{trackingStart ? fmtDateTime(trackingStart) : "All history (no start date)"}</div>
          </div>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed flex-1 min-w-[240px]">Calls and texts created before the tracking start are ignored by the OpenPhone sync and by webhooks, so analytics, SLA timers, anomalies and reports only reflect activity from that moment on. Resetting wipes every tracked record (calls, texts, alerts, anomalies, logs, report history) and keeps your settings, admins, roster and schedules.</p>
        <Button data-testid="reset-tracking-button" size="sm" variant="outline" className="h-9 text-xs border-red-500/40 text-red-300 bg-[#0f172a] hover:bg-red-500/10" onClick={() => { setWhen(toLocalInput()); setOpen(true); }}><AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Reset tracked data…</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#2d3d58] max-w-lg" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-red-300">Reset all tracked data</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">This permanently deletes every call, text, thread, alert, anomaly, email/Slack log and report run. Settings, admin accounts, the OpenPhone roster and report schedules are kept. Tracking restarts from the moment you choose.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="eyebrow block mb-1.5">Start tracking from (your local time)</label>
            <Input data-testid="reset-start-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-9 bg-[#0f172a] border-[#1f293d] font-mono text-xs" />
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Type <span className="text-red-300 font-mono">RESET</span> to confirm</label>
            <Input data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" className="h-9 bg-[#0f172a] border-[#1f293d] font-mono" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="reset-confirm-button" size="sm" className="bg-red-600 hover:bg-red-500" disabled={confirm !== "RESET" || !when || m.isPending} onClick={() => m.mutate()}>{m.isPending ? <Spinner className="text-white" /> : "Delete everything & restart tracking"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
